import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal, Form } from 'react-bootstrap';
import { FaMotorcycle, FaClock, FaCheckCircle, FaMapMarkerAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getOrdersApi, updateOrderStatusApi } from '../menu/service/api_service';
import { API_BASE_URL } from '../../config/api';

export default function DeliveryBoard() {
  const token = localStorage.getItem('token');
  const [orders, setOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  
  const fetchOrders = async () => {
    try {
      const res = await getOrdersApi(token, 'ready');
      const data = res.data;
      if (Array.isArray(data)) {
        setOrders(data);
      } else {
        console.error('Invalid data format:', data);
        setOrders([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
    
    // Setup SSE for real-time updates
    const eventSource = new EventSource(`${API_BASE_URL}/api/orders/stream?token=${token}`);
    
    eventSource.addEventListener('ORDER_UPDATED', (e) => {
      const updatedOrder = JSON.parse(e.data);
      if (updatedOrder.status === 'ready') {
        // Play notification sound for new delivery
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
        oscillator.connect(audioCtx.destination);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.15);
        
        setOrders(prev => {
          if (!prev.find(o => o.id === updatedOrder.id)) {
            return [updatedOrder, ...prev];
          }
          return prev;
        });
      } else if (updatedOrder.status !== 'ready') {
        setOrders(prev => prev.filter(o => o.id !== updatedOrder.id));
      }
    });

    return () => {
      eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCompleteClick = (order) => {
    setCurrentOrder(order);
    setProofFile(null);
    setShowModal(true);
  };

  const markCompleted = async (e) => {
    e.preventDefault();
    if (!currentOrder) return;
    
    try {
      const payload = { 
        status: 'completed',
        delivery_proof_image_url: null
      };

      const formData = new FormData();
      formData.append("orderData", JSON.stringify(payload));
      if (proofFile) {
        formData.append("delivery_proof", proofFile);
      }

      await updateOrderStatusApi(token, currentOrder.id, formData);
      setOrders(prev => prev.filter(o => o.id !== currentOrder.id));
      setShowModal(false);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Network error", "error");
    }
  };

  return (
    <Container fluid className="px-2 py-3" style={{ backgroundColor: "#f4f7fa", minHeight: "100%" }}>
      <h4 className="fw-bold text-dark mb-4 px-2"><FaMotorcycle className="me-2 text-primary" /> Delivery Board</h4>
      <Row className="g-3 px-2">
        {orders.length === 0 ? (
           <Col xs={12} className="text-center py-5 text-muted">
             <FaMotorcycle size={48} className="mb-3 opacity-25" />
             <h5>No pending deliveries</h5>
           </Col>
        ) : orders.map(order => (
          <Col xs={12} sm={6} md={6} lg={4} xl={3} key={order.id}>
            <Card className="border-0 shadow-sm h-100 rounded-4" style={{ overflow: 'hidden' }}>
              <div style={{ height: '4px', backgroundColor: '#0d6efd', width: '100%' }}></div>
              <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
                <Badge bg="primary" className="fs-6 py-2 px-3 rounded-pill">#{order.id}</Badge>
                <div className="text-end">
                  <div className="text-muted small fw-medium">
                    Order: {new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                  <div className="text-danger fw-bold small">
                    <FaClock className="me-1"/> 
                    Due: {order.delivery_time ? new Date(order.delivery_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'ASAP'}
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="d-flex flex-column">
                <div className="mb-3 pb-3 border-bottom">
                  <div className="fw-bold text-dark d-flex align-items-start gap-2" style={{ fontSize: '1.2rem' }}>
                    <FaMapMarkerAlt className="text-danger mt-1" />
                    <span>{order.recipient_name || "Unknown Location"}</span>
                  </div>
                </div>
                
                <div className="d-flex flex-column gap-2 mb-4 flex-grow-1">
                   <div className="text-muted small fw-bold">Items to Deliver:</div>
                  {order.items && order.items.map((item, idx) => (
                    <div key={idx} className="d-flex justify-content-between text-dark fw-medium">
                      <span>{item.quantity}x {item.product_name}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="primary" 
                  className="w-100 py-3 fw-bold d-flex justify-content-center align-items-center shadow-sm rounded-3 mt-auto"
                  style={{ fontSize: '1.2rem', transition: 'all 0.2s' }}
                  onClick={() => handleCompleteClick(order)}
                >
                  <FaCheckCircle className="me-2" /> Complete Delivery
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Completion Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-primary">Finish Order #{currentOrder?.id}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={markCompleted}>
          <Modal.Body>
            <p className="text-muted mb-4">You are about to mark this order as delivered to <strong>{currentOrder?.recipient_name}</strong>.</p>
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small text-muted">Delivery Proof Image (รูปภาพหลักฐานส่งสินค้า)</Form.Label>
              <Form.Control 
                type="file" 
                accept="image/*" 
                onChange={e => setProofFile(e.target.files[0] || null)}
              />
              <Form.Text className="text-muted">
                เลือกรูปถ่ายของหลักฐานเพื่อยืนยันการจัดส่งให้กับลูกค้า
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary" className="px-4 fw-bold shadow-sm">Confirm Delivery</Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
}
